import { useState, useEffect } from "react";
import { Cookie, X, CheckCircle, Settings } from "lucide-react";
import { Button } from "./ui/button";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    savePreferences(allPreferences);
  };

  const handleRejectAll = () => {
    const minimalPreferences = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    savePreferences(minimalPreferences);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const savePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      timestamp: new Date().toISOString(),
      preferences: prefs
    }));
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] transition-opacity"
        onClick={() => setShowSettings(false)}
      />

      {/* Cookie Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[101] bg-white border-t-4 border-blue-600 shadow-2xl animate-slide-up"
        data-testid="cookie-banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {!showSettings ? (
            // Simple Banner
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Wij gebruiken cookies</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Wij gebruiken cookies om uw ervaring op onze website te verbeteren, om verkeer te analyseren 
                    en om inhoud te personaliseren. Door op "Alles accepteren" te klikken, stemt u in met ons gebruik van cookies.
                  </p>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium mt-2 inline-flex items-center gap-1"
                  >
                    <Settings className="w-4 h-4" />
                    Cookie-instellingen aanpassen
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="btn-outline w-full sm:w-auto"
                  data-testid="reject-cookies"
                >
                  Alleen noodzakelijke
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="accept-cookies"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Alles accepteren
                </Button>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Cookie-instellingen</h3>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
                  aria-label="Sluiten"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Noodzakelijke Cookies */}
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-slate-900">Noodzakelijke cookies</h4>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">Verplicht</span>
                      </div>
                      <p className="text-sm text-slate-600">
                        Deze cookies zijn essentieel voor de werking van de website en kunnen niet worden uitgeschakeld. 
                        Ze worden meestal alleen ingesteld als reactie op acties die u onderneemt, zoals het instellen van uw privacyvoorkeuren.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-12 h-6 bg-green-500 rounded-full relative cursor-not-allowed opacity-50">
                        <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analytische Cookies */}
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 mb-2">Analytische cookies</h4>
                      <p className="text-sm text-slate-600">
                        Deze cookies stellen ons in staat om bezoeken en verkeersbronnen te tellen, zodat we de prestaties 
                        van onze site kunnen meten en verbeteren. Ze helpen ons te weten welke pagina's het meest en het minst populair zijn.
                      </p>
                    </div>
                    <button
                      onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
                      className="flex-shrink-0"
                      aria-label="Analytische cookies in-/uitschakelen"
                    >
                      <div className={`w-12 h-6 rounded-full relative transition-colors ${
                        preferences.analytics ? 'bg-blue-600' : 'bg-slate-300'
                      }`}>
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.analytics ? 'right-1' : 'left-1'
                        }`} />
                      </div>
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 mb-2">Marketing cookies</h4>
                      <p className="text-sm text-slate-600">
                        Deze cookies kunnen door onze advertentiepartners via onze website worden geplaatst. 
                        Ze kunnen door deze bedrijven worden gebruikt om een profiel van uw interesses op te bouwen en u relevante advertenties te tonen.
                      </p>
                    </div>
                    <button
                      onClick={() => setPreferences(prev => ({ ...prev, marketing: !prev.marketing }))}
                      className="flex-shrink-0"
                      aria-label="Marketing cookies in-/uitschakelen"
                    >
                      <div className={`w-12 h-6 rounded-full relative transition-colors ${
                        preferences.marketing ? 'bg-blue-600' : 'bg-slate-300'
                      }`}>
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.marketing ? 'right-1' : 'left-1'
                        }`} />
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200">
                <a 
                  href="/privacy" 
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Meer informatie over cookies
                </a>
                <div className="flex-1" />
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="btn-outline w-full sm:w-auto"
                >
                  Alleen noodzakelijke
                </Button>
                <Button
                  onClick={handleSavePreferences}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="save-preferences"
                >
                  Voorkeuren opslaan
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
}